import { useEffect } from "react";
import { useMap } from "react-leaflet";
import ReactDOMServer from "react-dom/server";

// Default styles to center the icon and clear Leaflet defaults
const DEFAULT_CONTAINER_STYLE = {
    backgroundImage: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'inherit'
};

/**
 * A reusable component that wraps a Leaflet control and injects React Icons into it.
 * 
 * @param {Object} props
 * @param {React.ComponentType} props.ControlComponent - The Leaflet control component (created via createControlComponent)
 * @param {Object} props.controlProps - Props to pass to the ControlComponent
 * @param {string} props.iconClass - The CSS class used to identify the static icon container
 * @param {React.ComponentType} props.Icon - The React Icon component for the static state
 * @param {string} props.loadingClass - The CSS class used to identify the loading icon container
 * @param {React.ComponentType} props.IconLoading - The React Icon component for the loading state
 * @param {Object} [props.iconProps] - Props passed to the icons (e.g., size)
 * @param {string} [props.loadingAnimationClass] - Optional CSS class for loading animation
 * @param {Object} [props.containerStyle] - Optional style object to apply to the icon container
 */
const LeafletReactIconControl = ({
    ControlComponent,
    controlProps,
    iconClass,
    Icon,
    loadingClass,
    IconLoading,
    iconProps = { size: 18 },
    loadingAnimationClass = "spin-animation",
    containerStyle = {}
}) => {
    const map = useMap();

    useEffect(() => {
        const injectIcon = (className, IconComponent, extraClass = "") => {
            if (!className || !IconComponent) return;

            const elements = map.getContainer().querySelectorAll(`.${className}`);
            elements.forEach(el => {
                const iconHtml = ReactDOMServer.renderToStaticMarkup(
                    <IconComponent {...iconProps} className={extraClass} style={{ verticalAlign: 'middle' }} />
                );

                if (el.innerHTML !== iconHtml) {
                    el.innerHTML = iconHtml;
                    // Apply container styles
                    Object.entries({...DEFAULT_CONTAINER_STYLE, ...containerStyle,}).forEach(([key, value]) => {
                        el.style[key] = value;
                    });
                }
            });
        };

        const updateIcons = () => {
            // Inject static icon
            if (iconClass && Icon) {
                injectIcon(iconClass, Icon);
            }
            // Inject loading icon
            if (loadingClass && IconLoading) {
                injectIcon(loadingClass, IconLoading, loadingAnimationClass);
            }
        };

        // Initial injection
        updateIcons();

        // Observer for state changes (e.g., library toggling classes)
        const controlContainer = map.getContainer().querySelector('.leaflet-control-container');
        if (controlContainer) {
            const observer = new MutationObserver(() => {
                updateIcons();
            });
            observer.observe(controlContainer, { subtree: true, attributes: true, childList: true });
            return () => observer.disconnect();
        }

    }, [map, iconClass, Icon, loadingClass, IconLoading, iconProps, loadingAnimationClass, containerStyle]);

    return <ControlComponent {...controlProps} />;
};

export default LeafletReactIconControl;
